import { useEffect, useId, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { PROJECT_FILE_KIND_LABELS, type ProjectFileView } from '@scoutaround/script-breakdown-shared';

import { ApiError, api } from '../../lib/api.js';
import { Icon } from '../Icon.js';
import { EmptyState } from '../ui/EmptyState.js';
import { MoreMenu, type MenuItem } from '../ui/MoreMenu.js';
import { StatusPill } from '../ui/StatusPill.js';
import { absoluteTime, formatBytes, personLabel, relativeTime } from '../references/format.js';
import { FileKindTile } from './FileCard.js';
import { fileShareUrl, fileStatus, useInvalidateFiles } from './model.js';

/**
 * THE OPEN FILE — the right-hand panel on a desk, the whole screen on a phone.
 *
 * What it is, how big, who uploaded it and when, and where it stands. NO DOWNLOAD:
 * every file is held until it has been checked, and file checking does not exist
 * yet, so the panel says plainly that the file is kept privately and will be
 * downloadable here once checking is available — never a button that cannot work.
 * Delete is a soft delete behind a confirmation (ADR 0011: ScoutAround keeps it).
 */
export function FileDetailPanel({
  projectId,
  fileId,
  file,
  loading,
  takeFocus,
  canDelete,
  deleteRefusal,
  onClose,
  onDeleted,
}: {
  projectId: string;
  fileId: string;
  /** False while somebody walks the list with the arrow keys: their focus stays there. */
  takeFocus: boolean;
  /** The file from the list; null when the list does not hold it. */
  file: ProjectFileView | null;
  loading: boolean;
  canDelete: boolean;
  /** Why Delete is not offered, when the reason is worth saying (read-only, the role). */
  deleteRefusal: string | null;
  onClose: () => void;
  onDeleted: (name: string) => void;
}) {
  const headingRef = useRef<HTMLHeadingElement>(null);

  // A newly opened file takes the reader's attention (on a phone it IS the screen) —
  // unless it was reached with the arrow keys, which must keep moving through the list.
  const loaded = file !== null;
  useEffect(() => {
    if (loaded && takeFocus) headingRef.current?.focus({ preventScroll: true });
  }, [fileId, loaded, takeFocus]);

  if (loading) {
    return (
      <div className="files-detail-inner" aria-busy="true" data-testid="files-detail-loading">
        <DetailBack onClose={onClose} />
        <div className="files-detail-head">
          <span className="ref-skeleton-line" style={{ width: '70%', height: 18 }} />
          <CloseButton onClose={onClose} />
        </div>
        <span className="ref-skeleton-line" style={{ width: '45%' }} />
      </div>
    );
  }

  if (file === null) {
    return (
      <div className="files-detail-inner" data-testid="files-detail-missing">
        <DetailBack onClose={onClose} />
        <div className="files-detail-head">
          <span />
          <CloseButton onClose={onClose} />
        </div>
        <EmptyState title="This file is not in Files">
          It may have been deleted, or the link belongs to another production.
        </EmptyState>
      </div>
    );
  }

  return (
    <LoadedDetail
      projectId={projectId}
      file={file}
      headingRef={headingRef}
      canDelete={canDelete}
      deleteRefusal={deleteRefusal}
      onClose={onClose}
      onDeleted={onDeleted}
    />
  );
}

/** A phone's way back to the list; the open file fills the screen there. */
function DetailBack({ onClose }: { onClose: () => void }) {
  return (
    <div className="files-detail-back-row">
      <button type="button" className="files-detail-back" data-testid="files-detail-back" onClick={onClose}>
        <Icon name="arrowLeft" size={14} />
        All files
      </button>
    </div>
  );
}

function CloseButton({ onClose }: { onClose: () => void }) {
  return (
    <button
      type="button"
      className="ctl ref-icon-btn files-detail-close"
      aria-label="Close the file"
      data-testid="files-detail-close"
      onClick={onClose}
    >
      <Icon name="close" size={16} />
    </button>
  );
}

function LoadedDetail({
  projectId,
  file,
  headingRef,
  canDelete,
  deleteRefusal,
  onClose,
  onDeleted,
}: {
  projectId: string;
  file: ProjectFileView;
  headingRef: React.RefObject<HTMLHeadingElement | null>;
  canDelete: boolean;
  deleteRefusal: string | null;
  onClose: () => void;
  onDeleted: (name: string) => void;
}) {
  const invalidate = useInvalidateFiles(projectId);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const titleId = useId();
  const status = fileStatus(file);

  const remove = useMutation({
    mutationFn: () => api.deleteFile(projectId, file.id),
    onSuccess: async () => {
      setConfirming(false);
      onDeleted(file.originalFilename);
      await invalidate();
    },
    onError: async (e) => {
      if (e instanceof ApiError && e.status === 404) {
        // Somebody else deleted it first: the list catches up and the panel says so.
        setConfirming(false);
        await invalidate();
        return;
      }
      setError(e instanceof ApiError && e.message !== '' ? e.message : 'The file could not be deleted. Try again.');
    },
  });

  const copyLink = async () => {
    const url = fileShareUrl(projectId, file.id);
    try {
      await navigator.clipboard.writeText(url);
      setCopied('Link copied.');
    } catch {
      setCopied(`Copy this link: ${url}`);
    }
  };

  const menu: MenuItem[] = [
    {
      id: 'copy',
      label: 'Copy link',
      testId: 'files-detail-copy',
      onSelect: () => {
        void copyLink();
      },
    },
    ...(canDelete
      ? [
          {
            id: 'delete',
            label: 'Delete file',
            tone: 'danger' as const,
            testId: 'files-detail-delete',
            onSelect: () => {
              setError(null);
              setConfirming(true);
            },
          },
        ]
      : []),
  ];

  return (
    <article className="files-detail-inner" aria-labelledby={titleId} data-testid={`files-detail-${file.id}`}>
      <DetailBack onClose={onClose} />
      <div className="files-detail-head">
        <FileKindTile kind={file.kind} filename={file.originalFilename} size="detail" testId="files-detail-kind" />
        <h2 id={titleId} ref={headingRef} tabIndex={-1} className="files-detail-title" data-testid="files-detail-title">
          {file.originalFilename}
        </h2>
        <div className="files-detail-top-actions">
          <MoreMenu label="File actions" testId="files-detail-menu" items={menu} />
          <CloseButton onClose={onClose} />
        </div>
      </div>

      {copied !== null ? (
        <p className="ref-quiet" role="status" data-testid="files-detail-copied">
          {copied}
        </p>
      ) : null}

      <dl className="files-facts" data-testid="files-detail-facts">
        <div>
          <dt>Type</dt>
          <dd data-testid="files-detail-type">{PROJECT_FILE_KIND_LABELS[file.kind]}</dd>
        </div>
        <div>
          <dt>Size</dt>
          <dd data-testid="files-detail-size">{formatBytes(file.byteSize)}</dd>
        </div>
        <div>
          <dt>Uploaded by</dt>
          <dd data-testid="files-detail-uploader">{personLabel(file.uploadedBy.displayName)}</dd>
        </div>
        <div>
          <dt>Uploaded</dt>
          <dd data-testid="files-detail-uploaded">
            <time dateTime={file.createdAt}>{absoluteTime(file.createdAt)}</time>
            <span className="files-facts-aside">{relativeTime(file.createdAt)}</span>
          </dd>
        </div>
        <div>
          <dt>Status</dt>
          <dd>
            <StatusPill state={status.state} label={status.label} testId="files-detail-status" />
          </dd>
        </div>
      </dl>

      <section className="files-private" aria-labelledby={`${titleId}-private`} data-testid="files-detail-private">
        <Icon name="lock" size={18} />
        <div>
          <p id={`${titleId}-private`} className="files-private-title">
            Kept privately
          </p>
          <p className="files-private-body">
            {file.validationStatus === 'PASSED'
              ? 'This file has been checked. Downloading files from the workspace is not available yet; ScoutAround keeps it privately for this production until it is.'
              : 'Every file is checked before anyone can download it. File checking is not available yet, so ScoutAround keeps this file privately for this production: it stays listed here, and it will be downloadable from here once checking is available.'}
          </p>
        </div>
      </section>

      {!canDelete && deleteRefusal !== null ? (
        <p className="ref-quiet" data-testid="files-detail-readonly">
          {deleteRefusal}
        </p>
      ) : null}

      {confirming ? (
        <div
          className="ref-confirm"
          role="alertdialog"
          aria-labelledby={`${titleId}-confirm`}
          aria-describedby={`${titleId}-confirm-body`}
          data-testid="files-detail-delete-confirm"
        >
          <p id={`${titleId}-confirm`} className="ref-confirm-title">
            Delete this file?
          </p>
          <p id={`${titleId}-confirm-body`} className="ref-confirm-body">
            {`“${file.originalFilename}” leaves Files for everyone on this production. Nothing is erased: ScoutAround keeps the file, so it can be recovered.`}
          </p>
          {error !== null ? (
            <p className="ref-error" role="alert" data-testid="files-detail-delete-error">
              {error}
            </p>
          ) : null}
          <div className="ref-confirm-actions">
            <button
              type="button"
              className="ctl"
              autoFocus
              data-testid="files-detail-delete-cancel"
              onClick={() => {
                setConfirming(false);
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn ref-danger-btn"
              disabled={remove.isPending}
              data-testid="files-detail-delete-submit"
              onClick={() => {
                remove.mutate();
              }}
            >
              {remove.isPending ? 'Deleting…' : 'Delete file'}
            </button>
          </div>
        </div>
      ) : null}
    </article>
  );
}
